import React, { useEffect, useState, useCallback } from 'react'
import { supabase } from '@/lib/supabase/client'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { RefreshCw, TrendingUp, TrendingDown, AlertTriangle, DollarSign, PieChart, BarChart3 } from 'lucide-react'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart as RechartsPieChart, Pie, Cell, Legend } from 'recharts'
import { useRefetchOnFocus } from '@/hooks/useRefetchOnFocus'
import { toast } from 'sonner'

interface OrcamentoResumo {
  ano: number
  valor_total_orcado: number
  valor_movimentado_saida: number
  valor_movimentado_entrada: number
  valor_contratado: number
  valor_verba_adicional: number
  valor_saldo_disponivel: number
  centro_resultado: string
  tipo_receita_despesa: string
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d']

export default function VisaoGeralOrcamentoPage() {
  const [data, setData] = useState<OrcamentoResumo[]>([])
  const [loading, setLoading] = useState(true)
  const [anoSelecionado, setAnoSelecionado] = useState<string>('')
  const [anos, setAnos] = useState<number[]>([])
  const anoRef = React.useRef(anoSelecionado)
  anoRef.current = anoSelecionado

  const fetchAnos = useCallback(async () => {
    try {
      const { data: anosData, error } = await supabase
        .from('solis_vw_orcamento_por_tipo_centro')
        .select('ano')
        .order('ano', { ascending: false })

      if (error) throw error

      const anosUnicos = [...new Set(anosData?.map((d: any) => d.ano) || [])].sort((a, b) => b - a) as number[]
      setAnos(anosUnicos)
      if (anosUnicos.length > 0 && !anoRef.current) {
        setAnoSelecionado(String(anosUnicos[0]))
      }
    } catch (error) {
      console.error('Erro ao buscar anos:', error)
      toast.error('Erro ao carregar anos. Tente atualizar.')
    }
  }, [])

  const fetchData = useCallback(async (ano?: string) => {
    const anoParaBuscar = ano || anoRef.current
    if (!anoParaBuscar) return
    setLoading(true)
    try {
      const { data: viewData, error } = await supabase
        .from('solis_vw_orcamento_por_tipo_centro')
        .select('*')
        .eq('ano', parseInt(anoParaBuscar))
        .order('valor_total_orcado', { ascending: false })

      if (error) throw error
      setData(viewData || [])
    } catch (error) {
      console.error('Erro ao buscar dados:', error)
      toast.error('Erro ao carregar dados. Tente atualizar.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchAnos()
  }, [fetchAnos])

  useEffect(() => {
    if (anoSelecionado) {
      fetchData(anoSelecionado)
    }
  }, [anoSelecionado, fetchData])

  const handleRefetch = useCallback(() => {
    if (anoRef.current) {
      fetchData(anoRef.current)
    } else {
      fetchAnos()
    }
  }, [fetchAnos, fetchData])

  useRefetchOnFocus(handleRefetch)

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value)
  }

  // Cálculos agregados
  const totalOrcado = data.reduce((sum, item) => sum + Number(item.valor_total_orcado || 0), 0)
  const totalContratado = data.reduce((sum, item) => sum + Number(item.valor_contratado || 0), 0)
  const totalSaldoDisponivel = data.reduce((sum, item) => sum + Number(item.valor_saldo_disponivel || 0), 0)
  const totalMovimentadoSaida = data.reduce((sum, item) => sum + Number(item.valor_movimentado_saida || 0), 0)
  const totalMovimentadoEntrada = data.reduce((sum, item) => sum + Number(item.valor_movimentado_entrada || 0), 0)
  const totalVerbaAdicional = data.reduce((sum, item) => sum + Number(item.valor_verba_adicional || 0), 0)

  const percentualUtilizado = totalOrcado > 0 ? ((totalContratado / totalOrcado) * 100) : 0
  const percentualDisponivel = totalOrcado > 0 ? ((totalSaldoDisponivel / totalOrcado) * 100) : 0

  // Dados para gráficos
  const dadosPorCentro = data.reduce((acc, item) => {
    const centro = item.centro_resultado || 'Sem centro'
    if (!acc[centro]) {
      acc[centro] = {
        centro,
        orcado: 0,
        contratado: 0,
        saldo: 0,
      }
    }
    acc[centro].orcado += Number(item.valor_total_orcado || 0)
    acc[centro].contratado += Number(item.valor_contratado || 0)
    acc[centro].saldo += Number(item.valor_saldo_disponivel || 0)
    return acc
  }, {} as Record<string, { centro: string; orcado: number; contratado: number; saldo: number }>)

  const graficoPorCentro = Object.values(dadosPorCentro)
    .sort((a, b) => b.orcado - a.orcado)
    .slice(0, 10)
    .map(item => ({
      name: item.centro.length > 20 ? item.centro.substring(0, 20) + '...' : item.centro,
      Orçado: item.orcado,
      Contratado: item.contratado,
      'Saldo Disponível': item.saldo,
    }))

  const dadosPorTipo = data.reduce((acc, item) => {
    const tipo = item.tipo_receita_despesa || 'Sem tipo'
    if (!acc[tipo]) {
      acc[tipo] = 0
    }
    acc[tipo] += Number(item.valor_total_orcado || 0)
    return acc
  }, {} as Record<string, number>)

  const graficoPorTipo = Object.entries(dadosPorTipo)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 8)
    .map(([name, value]) => ({
      name: name.length > 25 ? name.substring(0, 25) + '...' : name,
      value,
    }))

  // Alertas
  const alertas = []
  if (percentualUtilizado > 90) {
    alertas.push({
      tipo: 'warning',
      mensagem: `Orçamento ${percentualUtilizado.toFixed(1)}% utilizado. Atenção!`,
      icon: AlertTriangle,
    })
  }
  if (totalSaldoDisponivel < 0) {
    alertas.push({
      tipo: 'error',
      mensagem: 'Saldo disponível negativo detectado!',
      icon: AlertTriangle,
    })
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Visão Geral do Orçamento</h2>
          <p className="text-muted-foreground text-sm">
            Dashboard executivo com indicadores principais e análise consolidada
          </p>
        </div>
        <div className="flex gap-2">
          <Select value={anoSelecionado} onValueChange={setAnoSelecionado}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Selecione o ano" />
            </SelectTrigger>
            <SelectContent>
              {anos.map((ano) => (
                <SelectItem key={ano} value={String(ano)}>
                  {ano}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={() => fetchData()} disabled={loading}>
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
        </div>
      </div>

      {/* Alertas */}
      {alertas.length > 0 && (
        <div className="space-y-2">
          {alertas.map((alerta, idx) => (
            <Card key={idx} className={`border-${alerta.tipo === 'error' ? 'red' : 'yellow'}-500 bg-${alerta.tipo === 'error' ? 'red' : 'yellow'}-50`}>
              <CardContent className="pt-6">
                <div className="flex items-center gap-2">
                  <alerta.icon className={`h-5 w-5 text-${alerta.tipo === 'error' ? 'red' : 'yellow'}-600`} />
                  <span className={`font-medium text-${alerta.tipo === 'error' ? 'red' : 'yellow'}-800`}>
                    {alerta.mensagem}
                  </span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* KPIs Principais */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Orçado</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{formatCurrency(totalOrcado)}</div>
            <p className="text-xs text-muted-foreground">Orçamento previsto para {anoSelecionado}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Contratado</CardTitle>
            <TrendingDown className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">{formatCurrency(totalContratado)}</div>
            <p className="text-xs text-muted-foreground">
              {percentualUtilizado.toFixed(1)}% do orçamento utilizado
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Saldo Disponível</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${totalSaldoDisponivel < 0 ? 'text-red-600' : 'text-blue-600'}`}>
              {formatCurrency(totalSaldoDisponivel)}
            </div>
            <p className="text-xs text-muted-foreground">
              {percentualDisponivel.toFixed(1)}% ainda disponível
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Movimentações</CardTitle>
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(totalMovimentadoEntrada - totalMovimentadoSaida)}
            </div>
            <p className="text-xs text-muted-foreground">
              Entrada: {formatCurrency(totalMovimentadoEntrada)} | Saída: {formatCurrency(totalMovimentadoSaida)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Gráficos */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Orçamento por Centro de Custo</CardTitle>
            <CardDescription>Top 10 centros de custo em {anoSelecionado}</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="h-[300px] flex items-center justify-center">Carregando...</div>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={graficoPorCentro}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                  <YAxis />
                  <Tooltip formatter={(value: number) => formatCurrency(value)} />
                  <Legend />
                  <Bar dataKey="Orçado" fill="#0088FE" />
                  <Bar dataKey="Contratado" fill="#FF8042" />
                  <Bar dataKey="Saldo Disponível" fill="#00C49F" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Distribuição por Tipo de Despesa</CardTitle>
            <CardDescription>Top 8 tipos de despesa em {anoSelecionado}</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="h-[300px] flex items-center justify-center">Carregando...</div>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <RechartsPieChart>
                  <Pie
                    data={graficoPorTipo}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {graficoPorTipo.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value: number) => formatCurrency(value)} />
                  <Legend />
                </RechartsPieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Resumo de Movimentações */}
      <Card>
        <CardHeader>
          <CardTitle>Resumo de Movimentações e Ajustes</CardTitle>
          <CardDescription>Entradas, saídas e verbas adicionais</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="p-4 border rounded-lg">
              <div className="text-sm text-muted-foreground mb-1">Movimentações de Entrada</div>
              <div className="text-2xl font-bold text-green-600">{formatCurrency(totalMovimentadoEntrada)}</div>
            </div>
            <div className="p-4 border rounded-lg">
              <div className="text-sm text-muted-foreground mb-1">Movimentações de Saída</div>
              <div className="text-2xl font-bold text-red-600">{formatCurrency(totalMovimentadoSaida)}</div>
            </div>
            <div className="p-4 border rounded-lg">
              <div className="text-sm text-muted-foreground mb-1">Verbas Adicionais</div>
              <div className="text-2xl font-bold text-blue-600">{formatCurrency(totalVerbaAdicional)}</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

